import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { supabase } from "../lib/supabaseClient";
import {
  setCurrentUser,
  clearCurrentUser,
  clearUserScopedStorage,
  isAdminEmail,
} from "../utils/auth";
import { fetchMyProfile } from "../utils/profiles";
import { logAudit } from "../utils/auditLog";
import { initStudyDataSync, hydrateStudyData } from "../utils/studyDataSync";
import { hydrateRequestedTrials } from "../utils/trialRequests";
import { identifyUser, resetUser } from "../lib/posthog";

// Sign a clinical-trials user (not admins or shop-only customers) out after
// this many milliseconds with no mouse / keyboard / touch / scroll activity.
// Activity in any browser tab keeps every tab alive via a shared `localStorage`
// timestamp + `storage` event.
const INACTIVITY_TIMEOUT_MS = 2 * 60 * 60 * 1000;
const ACTIVITY_KEY = "qentoros-last-activity";
const ACTIVITY_THROTTLE_MS = 1000;

const AuthContext = createContext({
  session: null,
  user: null,
  email: "",
  isAdmin: false,
  profile: null,
  profileLoading: true,
  shopAccess: false,
  trialsAccess: false,
  loading: true,
  signIn: async () => ({ error: new Error("AuthProvider not mounted") }),
  signOut: async () => {},
  refreshProfile: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  // The user's profile row carries the shop/trials access flags. `profileError`
  // is true when the table couldn't be reached at all (not configured / not
  // migrated yet) — the trials guard fails open on that so we never regress the
  // existing dashboard, while the new shop surface always fails closed.
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(false);
  // False until the inactivity window has been initialized for the current
  // session. Lets us treat a new sign-in (or the initial mount) as fresh activity
  // (so it doesn't instantly log out on a stale timestamp) while still honoring
  // the last real activity time on later re-runs (e.g. token refresh). Reset on
  // sign-out, so the next sign-in in the same tab starts a fresh window.
  const activityInitializedRef = useRef(false);
  // True once a signed-in session has been applied on this page load. Lets the
  // signed-out branch distinguish an actual logout (wipe user-scoped storage)
  // from an anonymous page load or startup race (leave storage alone).
  const hadSessionRef = useRef(false);
  // The user id currently identified to PostHog, or null. Same distinction as
  // `hadSessionRef`: it keeps an anonymous page load from calling reset().
  const analyticsUserRef = useRef(null);
  // The user id the profile in state belongs to, or null when none is held.
  // What makes a profile load a *re-check* rather than a first load — see
  // loadProfile, where that distinction is the whole point.
  const profileUserRef = useRef(null);
  // Monotonic id for profile loads, so a slow response can't overwrite a newer
  // one. Two loads overlap routinely: a token refresh fires while the initial
  // getSession() load is still in flight.
  const profileRequestRef = useRef(0);

  // Load (or silently re-check) the signed-in user's profile row.
  //
  // `profileLoading` means "we do not yet know who this user is" — NOT "a fetch
  // is in flight". The difference is load-bearing: every route guard
  // (ProtectedRoute, AdminRoute, ShopRoute, ShopGate) renders a spinner INSTEAD
  // of its children while it is true, so flipping it unmounts whatever page the
  // user is on and remounts it with fresh state. Supabase re-checks the session
  // whenever a tab regains visibility and emits an auth event for it, so
  // treating those re-checks as loads meant switching browser tabs silently
  // discarded any in-progress work — a half-filled tracking number in the
  // Orders dialog, a half-typed new user in User access. Same user, profile
  // already in hand => refresh in place, no spinner, nothing unmounts.
  const loadProfile = useCallback(async (activeSession) => {
    const userId = activeSession?.user?.id ?? null;
    const requestId = ++profileRequestRef.current;

    if (!userId) {
      profileUserRef.current = null;
      setProfile(null);
      setProfileError(false);
      setProfileLoading(false);
      return;
    }

    const isRecheck = userId === profileUserRef.current;
    if (!isRecheck) setProfileLoading(true);

    const { profile: row, unavailable } = await fetchMyProfile(userId);

    // A newer load started while this one was in flight (or the user changed).
    // Its result is the current truth; this one is stale.
    if (requestId !== profileRequestRef.current) return;

    if (isRecheck && unavailable) {
      // One failed request on a background re-check must not cost the user
      // their access. Dropping a profile we already trust would flip is_admin /
      // shop_access / trials_access to false and bounce an admin out of the
      // page they are working in, over a blip. Keep what we have.
      setProfileLoading(false);
      return;
    }

    profileUserRef.current = userId;
    setProfile(row);
    setProfileError(Boolean(unavailable));
    setProfileLoading(false);
  }, []);

  useEffect(() => {
    let isMounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!isMounted) return;
      applySession(data.session);
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, nextSession) => {
        applySession(nextSession);
      }
    );

    function applySession(nextSession) {
      // Keep the previous session OBJECT when nothing about it actually
      // changed. Supabase re-emits SIGNED_IN on tab focus with an identical
      // session; storing the new object would change the context value's
      // identity and re-render every consumer for no reason.
      setSession((prev) =>
        prev &&
        nextSession &&
        prev.access_token === nextSession.access_token &&
        prev.user?.id === nextSession.user?.id
          ? prev
          : nextSession
      );
      const email = nextSession?.user?.email;
      if (email) {
        setCurrentUser(email);
        // Study data (patients/trials/forms) is centralized in Supabase. Attach
        // the write-through listeners and pull this user's records into the
        // local cache so a fresh device / cleared browser re-hydrates. Both are
        // idempotent and degrade to no-ops when Supabase isn't configured.
        initStudyDataSync();
        hydrateStudyData();
        // Pull this user's trial access requests into the localStorage cache so
        // the "Requested" state follows them across devices. Idempotent no-op
        // when Supabase isn't configured.
        hydrateRequestedTrials();
        hadSessionRef.current = true;
      } else {
        // Only wipe on a real signed-in -> signed-out transition. Everything
        // wiped is a hydrated cache of Supabase data (or per-user state we
        // deliberately drop), so the next login restores it — this is what
        // keeps one user's patients/forms/audit trail from leaking to the next
        // person on a shared clinic workstation.
        if (hadSessionRef.current) {
          clearUserScopedStorage();
          hadSessionRef.current = false;
        }
        clearCurrentUser();
      }
      loadProfile(nextSession);
    }

    return () => {
      isMounted = false;
      listener?.subscription?.unsubscribe?.();
    };
  }, [loadProfile]);

  // The idle timeout only applies to clinical-trials users, whose dashboard holds
  // study and patient records. Shop-only customers stay signed in like on any
  // store, and admins are exempt. Read from the profile row itself rather than
  // the context's trialsAccess, which also counts admins and fails open when
  // the profile can't be loaded; until the profile loads, no timer runs.
  const sessionEmail = session?.user?.email ?? "";
  const idleTimeoutApplies =
    Boolean(session) &&
    profile?.trials_access === true &&
    profile?.is_admin !== true &&
    !isAdminEmail(sessionEmail);

  useEffect(() => {
    if (!idleTimeoutApplies) {
      // Signed out (or not subject to the timeout): the next time the timer
      // starts is a new window, not a continuation. If this stayed true, a
      // sign-in later in the same tab would inherit the previous session's
      // last-activity stamp — long past the timeout — and be signed out the
      // instant it started, on every retry until a reload.
      activityInitializedRef.current = false;
      return undefined;
    }

    let timeoutId;
    let lastWrite = 0;

    const expireSession = () => {
      // Write the audit row first so it is dispatched under the user's
      // still-valid JWT. `logAudit` records to localStorage synchronously and
      // kicks off the Supabase insert before `signOut()` invalidates the
      // session locally — same invariant the sidebar logout button relies on.
      logAudit({
        action: "auth.logout",
        entityType: "session",
        details: {
          reason: "inactivity",
          inactivityMs: INACTIVITY_TIMEOUT_MS,
        },
        source: "AuthContext",
      });
      supabase.auth.signOut();
    };

    const schedule = (fromTs) => {
      clearTimeout(timeoutId);
      const remaining = Math.max(
        INACTIVITY_TIMEOUT_MS - (Date.now() - fromTs),
        0
      );
      timeoutId = setTimeout(expireSession, remaining);
    };

    const markActive = () => {
      const now = Date.now();
      if (now - lastWrite < ACTIVITY_THROTTLE_MS) return;
      lastWrite = now;
      try {
        localStorage.setItem(ACTIVITY_KEY, String(now));
      } catch {
        // localStorage may be unavailable (private mode, quota); the
        // in-memory timer still works for this tab.
      }
      schedule(now);
    };

    const onStorage = (e) => {
      if (e.key !== ACTIVITY_KEY || !e.newValue) return;
      const ts = Number(e.newValue);
      if (!Number.isFinite(ts)) return;
      schedule(ts);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") markActive();
    };

    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "touchstart",
      "scroll",
    ];
    events.forEach((ev) =>
      window.addEventListener(ev, markActive, { passive: true })
    );
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisibility);

    let initial = Date.now();
    if (!activityInitializedRef.current) {
      // First run for this page load: arriving/reloading the app is itself an
      // interaction, so start a fresh window and stamp "now". This prevents a
      // returning user with a still-valid session (whose last activity is older
      // than the timeout) from being signed out the instant the page loads.
      activityInitializedRef.current = true;
      try {
        localStorage.setItem(ACTIVITY_KEY, String(initial));
      } catch {
        // ignore
      }
    } else {
      // Later re-runs (the session object changed, e.g. a background token
      // refresh) must not reset the clock — honor the last real activity time
      // so an idle-but-open tab still times out on schedule.
      try {
        const stored = Number(localStorage.getItem(ACTIVITY_KEY));
        if (Number.isFinite(stored) && stored > 0) initial = stored;
      } catch {
        // ignore
      }
    }
    schedule(initial);

    return () => {
      clearTimeout(timeoutId);
      events.forEach((ev) => window.removeEventListener(ev, markActive));
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [session, idleTimeoutApplies]);

  const value = useMemo(() => {
    const user = session?.user ?? null;
    const email = user?.email ?? "";
    // Admin = a hardcoded founder (permanent, lockout-proof floor) OR a profile
    // flagged is_admin (managed dynamically from the User Access page). Mirrors
    // the server-side public.is_qentoros_admin() SQL function.
    const isAdmin = isAdminEmail(email) || profile?.is_admin === true;
    // Admins implicitly have both accesses. The trials dashboard is a
    // pre-existing surface, so it fails open when the profile table can't be
    // reached (profileError) to avoid locking out current users before the
    // migration runs. The shop is new and always fails closed — access
    // requires an explicit shop_access = true.
    const shopAccess = isAdmin || profile?.shop_access === true;
    const trialsAccess =
      isAdmin ||
      profile?.trials_access === true ||
      (profileError && Boolean(session));
    return {
      session,
      user,
      email,
      isAdmin,
      profile,
      profileLoading,
      shopAccess,
      trialsAccess,
      loading,
      refreshProfile: () => loadProfile(session),
      signIn: async ({ email: emailArg, password, captchaToken }) => {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: emailArg,
          password,
          // Forwarded to Supabase Auth's built-in CAPTCHA protection. Enable it
          // (Dashboard -> Authentication -> Attack Protection -> Turnstile) and
          // set the Turnstile secret key there for this to be enforced.
          options: captchaToken ? { captchaToken } : undefined,
        });
        return { data, error };
      },
      signOut: async () => {
        await supabase.auth.signOut();
      },
    };
  }, [session, loading, profile, profileLoading, profileError, loadProfile]);

  // Keep the analytics identity in step with the session. Waits for the profile
  // to settle so the access flags are written once, correctly, rather than
  // first as all-false and then again a moment later.
  //
  // reset() is deliberately limited to a real signed-in -> signed-out
  // transition: it also discards the anonymous id and campaign attribution, so
  // running it on every anonymous page load would break marketing funnels — and
  // running it on none would leave the last vet's identity attached to the next
  // person on a shared clinic workstation.
  const { user, email, isAdmin, shopAccess, trialsAccess } = value;
  useEffect(() => {
    if (!user?.id) {
      if (analyticsUserRef.current) {
        analyticsUserRef.current = null;
        resetUser();
      }
      return;
    }
    if (profileLoading) return;
    analyticsUserRef.current = user.id;
    identifyUser({
      id: user.id,
      email,
      isAdmin,
      shopAccess,
      trialsAccess,
    });
    // Keyed on the id rather than the user object: a background token refresh
    // hands back a new object for the same person and must not re-identify.
  }, [user?.id, email, isAdmin, shopAccess, trialsAccess, profileLoading]);

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
